import { Button, Dialog, Field, Input } from '@cloudflare/kumo';
import { useEffect, useState, type ReactNode } from 'react';
import { ICONS } from './icons';
import { DangerButton } from './DangerButton';

/**
 * Diálogo de confirmação sobre o `Modal` do Cloudscape.
 *
 * O LeafyGreen tinha dois componentes (`ConfirmationModal` e
 * `DangerConfirmModal`); aqui há um só, com duas variantes:
 *  - `ConfirmDialog`: confirmação simples (publicar, retomar, finalizar);
 *  - `TypedConfirmDialog`: ação destrutiva, em que o botão só libera depois
 *    de o usuário digitar `DELETE`, o nome do item ou o texto exigido.
 */

export interface ConfirmDialogProps {
  /** Abre/fecha o diálogo. */
  open: boolean;
  title: ReactNode;
  /** Conteúdo explicativo. */
  children?: ReactNode;
  /** Texto do botão de confirmação. */
  confirmText: string;
  /** `danger` usa o botão destrutivo; `primary` o botão primário do Cloudscape. */
  variant?: 'danger' | 'primary';
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({ open, title, children, confirmText, variant = 'primary', onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(o: boolean) => { if (!o) (onCancel)(); }}>
<Dialog size="sm" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h2 className="text-xl font-semibold">{title}</h2>}</Dialog.Title>

      {children}
    <div className="mt-6 flex justify-end gap-2">{<div className="float-right">
          <div className="flex flex-wrap gap-2 items-center">
            <Button variant="ghost" type="button" onClick={onCancel}>Cancelar</Button>
            {variant === 'danger'
              ? <DangerButton type="button" onClick={onConfirm}>{confirmText}</DangerButton>
              : <Button variant="primary" type="button" onClick={onConfirm}>{confirmText}</Button>}
          </div>
        </div>}</div>
</Dialog>
</Dialog.Root>
  );
}

/** Palavra padrão que libera a confirmação destrutiva. */
export const DELETE_WORD = 'DELETE';

export function confirmationMatches(typed: string, requiredText?: string, objectName?: string | null) {
  const t = typed.trim();
  if (requiredText) return t === requiredText;
  if (t.toUpperCase() === DELETE_WORD) return true;
  const name = (objectName || '').trim();
  return !!name && t.toLowerCase() === name.toLowerCase();
}

export interface TypedConfirmDialogProps extends ConfirmDialogProps {
  /** Texto exato a digitar (ex.: ESQUECER). Sem ele, vale "DELETE" ou o nome do item. */
  requiredText?: string;
  /** Nome do que vai ser excluído: digitar o nome também libera o botão. */
  objectName?: string | null;
}

export function TypedConfirmDialog({ open, title, children, confirmText, requiredText, objectName, onConfirm, onCancel }: TypedConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  useEffect(() => { if (open) setTyped(''); }, [open]);

  const allowed = confirmationMatches(typed, requiredText, objectName);
  const name = (objectName || '').trim();
  // texto sugerido: o exigido, ou o nome do item (o DELETE também vale, mas o nome é mais claro)
  const hint = requiredText || name || DELETE_WORD;
  const copy = async () => { try { await navigator.clipboard.writeText(hint); } catch { /* sem permissão */ } };
  const label = (
    <span className="inline-flex flex-wrap items-center gap-2">
      Digite
      <code className="inline-flex items-center gap-1 rounded border border-dashed border-kumo-line bg-kumo-tint px-2 py-0.5 text-sm">
        {hint}
        <button type="button" onClick={copy} aria-label="Copiar texto de confirmação" className="text-kumo-subtle hover:text-kumo-default"><ICONS.Copy size={14} /></button>
      </code>
      para confirmar
    </span>
  );

  return (
    <Dialog.Root open={open} onOpenChange={(o: boolean) => { if (!o) (onCancel)(); }}>
<Dialog size="sm" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<div className="flex flex-wrap gap-2 items-center items-center">
          <span>{title}</span>
        </div>}</Dialog.Title>

      <div className="flex flex-col gap-4">
        <div className="text-kumo-subtle">{children}</div>
        <Field label={label}>
          <Input onChange={(e) => setTyped(e.target.value)} aria-label="Confirmação" value={typed} placeholder={hint} autoComplete="off" autoFocus />
        </Field>
      </div>
    <div className="mt-6 flex items-center justify-between gap-2">
      <Button variant="ghost" type="button" onClick={onCancel}>Cancelar</Button>
      <DangerButton type="button" disabled={!allowed} onClick={onConfirm}>{confirmText}</DangerButton>
    </div>
</Dialog>
</Dialog.Root>
  );
}
